import styled from 'styled-components'
import { Badge } from '../../../atoms/Badge'

export const DetailPanel = styled.div<{ $isOpen: boolean }>`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  background: ${({ theme }) => theme.colors.background.primary};
  transform: translateX(${({ $isOpen }) => ($isOpen ? '0' : '100%')});
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: 100;
  max-width: 100%;

  /* Ensure all child elements respect parent width */
  & > * {
    max-width: 100%;
    min-width: 0;
  }
`

export const DetailHeader = styled.div`
  background: ${({ theme }) => theme.colors.background.primary};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
  padding: ${({ theme }) => theme.spacing.medium};
  position: sticky;
  top: 0;
  z-index: 10;
`

export const DetailHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.medium};
`

export const BackButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small}
    ${({ theme }) => theme.spacing.medium};
  background: transparent;
  border: 1px solid ${({ theme }) => theme.colors.border.default};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  color: ${({ theme }) => theme.colors.text.primary};
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};

  &:hover {
    background: ${({ theme }) => theme.colors.background.secondary};
    transform: translateX(-2px);
  }
`

export const DetailTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
  flex: 1;
`

export const DetailContent = styled.div`
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  padding: ${({ theme }) => theme.spacing.large};
  max-width: 100%;
  min-width: 0;

  /* Ensure all child elements respect container width */
  & > * {
    max-width: 100%;
    min-width: 0;
  }
`

export const DetailSection = styled.div`
  margin-bottom: ${({ theme }) => theme.spacing.xlarge};

  &:last-child {
    margin-bottom: 0;
  }
`

export const EventDetailSectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.medium} 0;
  padding-bottom: ${({ theme }) => theme.spacing.small};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border.default};
`

export const DetailGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};
`

export const DetailItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const DetailLabel = styled.span`
  font-size: 12px;
  font-weight: 500;
  color: ${({ theme }) => theme.colors.text.tertiary};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

export const DetailValue = styled.span`
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};
  font-weight: 500;
`

export const HeroSection = styled.div`
  position: relative;
  width: 100%;
  height: 300px;
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  overflow: hidden;
  margin-bottom: ${({ theme }) => theme.spacing.large};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

export const PriceSection = styled.div`
  background: ${({ theme }) => theme.colors.background.secondary};
  padding: ${({ theme }) => theme.spacing.large};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const PriceMain = styled.div`
  font-size: 28px;
  font-weight: 700;
  color: ${({ theme }) => theme.colors.text.primary};
  margin-bottom: ${({ theme }) => theme.spacing.small};
`

export const PriceSub = styled.div`
  font-size: 16px;
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: ${({ theme }) => theme.spacing.medium};
  margin-top: ${({ theme }) => theme.spacing.medium};
`

export const FeatureItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.small};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 14px;
  color: ${({ theme }) => theme.colors.text.primary};

  svg {
    width: 16px;
    height: 16px;
    color: ${({ theme }) => theme.colors.text.tertiary};
  }
`

export const StatusBadge = styled.span<{ $status?: string }>`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  background: ${({ theme, $status }) => {
    const statusLower = $status?.toLowerCase()
    if (statusLower === 'active' || statusLower === 'approved')
      return theme.colors.successLight
    if (statusLower === 'inactive' || statusLower === 'rejected')
      return 'rgba(255, 59, 48, 0.1)'
    if (statusLower === 'pending') return theme.colors.warningLight
    return theme.colors.background.tertiary
  }};
  color: ${({ theme, $status }) => {
    const statusLower = $status?.toLowerCase()
    if (statusLower === 'active' || statusLower === 'approved')
      return theme.colors.success
    if (statusLower === 'inactive' || statusLower === 'rejected')
      return theme.colors.danger
    if (statusLower === 'pending') return theme.colors.warning
    return theme.colors.text.secondary
  }};
`

export const DescriptionSection = styled.div`
  padding: ${({ theme }) => theme.spacing.large};
  background: ${({ theme }) => theme.colors.background.secondary};
  border-radius: ${({ theme }) => theme.borderRadius.large};
  margin-bottom: ${({ theme }) => theme.spacing.large};
`

export const DescriptionText = styled.p`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0;
`

// Styled components from ListingDetail for consistent design
export const PropertyTitle = styled.h1`
  font-size: ${({ theme }) => theme.typography.fontSize.hero};
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text.primary};
  margin: 0 0 ${({ theme }) => theme.spacing.small} 0;
  line-height: ${({ theme }) => theme.typography.lineHeight.tight};
`

export const PropertyAddress = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.small};
  color: ${({ theme }) => theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  margin-bottom: ${({ theme }) => theme.spacing.large};

  svg {
    color: ${({ theme }) => theme.colors.primary};
  }
`

export const MainPrice = styled.div`
  font-size: 36px;
  font-weight: ${({ theme }) => theme.typography.fontWeight.bold};
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1;
`

export const PricePerMeter = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.section};
  color: ${({ theme }) => theme.colors.text.secondary};
`

export const ListingMetaCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.small};
  padding: ${({ theme }) => theme.spacing.medium};
  background: ${({ theme }) => theme.colors.background.tertiary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  margin-top: ${({ theme }) => theme.spacing.large};
`

export const ListingMetaItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing.medium};
`

export const ListingMetaLabel = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  color: ${({ theme }) => theme.colors.text.tertiary};
`

export const ListingMetaValue = styled.span`
  font-size: ${({ theme }) => theme.typography.fontSize.small};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  color: ${({ theme }) => theme.colors.text.primary};
`

export const Description = styled.div`
  font-size: ${({ theme }) => theme.typography.fontSize.body};
  line-height: ${({ theme }) => theme.typography.lineHeight.relaxed};
  color: ${({ theme }) => theme.colors.text.secondary};
  white-space: pre-wrap;
  padding: 0 ${({ theme }) => theme.spacing.large};
`

export const StatusBadgeStyled = styled(Badge)<{
  $variant?: string
  $size?: string
}>`
  font-size: ${({ theme }) => theme.typography.fontSize.micro};
`
